import { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import dashboardService from "@plannel/services/dashboard/dashboard-service";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import CodeUtil from "../utils/code-util";
import numberUtil from "@plannel/utils/number-util";
import TypeToggleButton from "@plannel/components/ToggleButton";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import { DashboardPeriodCaption, baseDatesToPeriod } from "@plannel/components/dashboard/DashboardLayout";
import DashboardComponent from "../../components/layout/DashboardComponent";
import CustomerHrchyTreeFilter from "@plannel/components/filter/CustomerHrchyTreeFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import {difference, groupBy, includes, isArray, isEmpty, isEqual, keys, round, size, sum} from "lodash"; //isNull,

const DpDashboard = ({ viewName, title }) => {
  const { toggleType: { AMT, QTY }, dpDashboardMeasure } = CodeUtil;
  const {
    SALES_ACTUAL, DEMAND_PLAN, COGS, YOY, FIN_ACHIEVE_P, DEMAND_PLAN_COGS,
    DEMAND_PLAN_GP, FINANCE_PLAN, FINANCE_PLAN_COGS, FINANCE_PLAN_GP, GROSS_MARGIN_P,
    MOM_GP_P, MOM_P, PLAN_VS_FIN_GP_P, PLAN_VS_FIN_P, SALES_ACTUAL_PROFIT, SALES_VS_FIN_GP_P,
    SALES_VS_PLAN_GP_P, SALES_VS_PLAN_P, YOY_COGS, YOY_GP, YOY_GP_P, YOY_P
  } = dpDashboardMeasure;
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const defaultYTD = {
    yoy: [],
    demand: [],
    budget: [],
  };

  const [stackBarData, setStackBarData] = useState({
    key: "YTD",
    customerHrchy: defaultYTD,
    itemHrchy: defaultYTD,
  });

  const [gridData, setGridData] = useState({ AMT: [], QTY: [] });
  const [chartData, setChartData] = useState([]);
  const [initialLoad, setInitialLoad] = useState(false);

  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? {});
  const [measureType, setMeasureType] = useState(reduxViewState?.measureType ?? AMT);
  const [currency, setCurrency] = useState(null);
  const { DP } = CodeUtil.module;

  const getRequestParams = () => {
    const params = {};
    params.currentDate = new ZDate();

    if (filterValue?.customerHrchy) {
      params.custHrchyCd = filterValue.customerHrchy.hrchyCd;
    }

    if (filterValue?.category) {
      params.itemHrchyCd = filterValue.category.hrchyCd;
    }

    return { ...params };
  };

  const getResultArray = (arrays, isAvg = false) => {
    if (!isEmpty(arrays)) {
      const numOfArrays = arrays.length;

      return arrays[0].map((_, idx) => {
        const sum = arrays.reduce((total, arr) => numberUtil.decimalAdd(total, arr[idx], 2), 0);
        return isAvg ? sum / numOfArrays : sum;
      });
    }
    return [];
  };

  const getPer = (target, value) => {
    const result = round((target / value) * 100, 2);
    if (isNaN(result) || !isFinite(result)) {
      return 0;
    }

    return result;
  };

  const getAllData = (ytdData, isLeaf, name = "All") => {
    const allData = [];
    const types = [QTY, AMT];

    if (isEmpty(ytdData) || isLeaf) {
      return allData;
    }

    for (const type of types) {
      const row = { hrchyNm: name, stackBarChartData: [0, 0, 0], type };
      const stackBarDatas = ytdData.filter(data => isEqual(data.type, type));
      const allDatas = stackBarDatas.map(data => data.stackBarChartData);
      const sumArray = getResultArray(allDatas);
      const targetVal = sumArray?.[0] ?? 0;
      const planVal = sumArray?.[1] ?? 0;
      row.stackBarChartData = [targetVal, planVal, getPer(targetVal, planVal)];

      allData.push(row);
    }

    return allData;
  };

  const setMaxValues = ytdData => {
    if (!isEmpty(ytdData)) {
      const types = [QTY, AMT];
      for (const type of types) {
        const allValueList = [];

        for (const ytdRow of ytdData) {
          if (isEqual(ytdRow.type, type)) {
            allValueList.push(...ytdRow.stackBarChartData);
          }
        }
        const maxvalue = Math.max(...allValueList);
        for (const ytdRow of ytdData) {
          if (isEqual(ytdRow.type, type)) {
            ytdRow.domainMaxValue = maxvalue;
          }
        }
      }
    }
  };

  const getLeafFlg = isCust => {
    const filterNm = isCust ? "customerHrchy" : "category";
    if (filterValue?.[filterNm]) {
      return filterValue?.[filterNm]?.leafFlg ?? false;
    }
    return false;
  };

  const getSortList = (list, key) => {
    if (!isEmpty(list)) {
      list.sort((a, b) => {
        const aPlan = a.stackBarChartData[1];
        const bPlan = b.stackBarChartData[1];
        const aActual = a.stackBarChartData[0];
        const bActual = b.stackBarChartData[0];

        if (aPlan !== bPlan) {
          return bPlan - aPlan;
        } else {
          return bActual - aActual;
        }
      });

      const tabList = [];

      for (const row of list) {
        row.tabName = key;
        tabList.push(row);
      }

      return tabList;
    }
    return list;
  };

  const getYtdData = (data, allName, isCust = true) => {
    const hrchyNm = isCust ? "custHrchyNm" : "itemHrchyNm";
    const leafFlg = getLeafFlg(isCust);
    const groupdYtdData = groupBy(data, hrchyNm);
    const YOY = [];
    const DP = [];
    const BUDGET = [];

    for (const hrchyKey of keys(groupdYtdData)) {
      const dataByHrchyNm = groupdYtdData[hrchyKey];
      if (hrchyKey !== "null") {
        let salesActualAmt = 0;
        let salesActualQty = 0;
        let yoyQty = 0;
        let yoyAmt = 0;
        let demandPlanAmt = 0;
        let demandPlanQty = 0;
        let budgetAmt = 0;
        let budgetQty = 0;

        for (const planData of dataByHrchyNm) {
          let sumAmt = 0;
          for (const amt of planData?.amt) {
            sumAmt = numberUtil.decimalAdd(sumAmt, amt, 5);
          }
          if (isEqual(planData.measure, "salesActual")) {
            salesActualAmt = numberUtil.decimalAdd(salesActualAmt, sumAmt, 2);
            salesActualQty = numberUtil.decimalAdd(salesActualQty, sum(planData.qty), 2);
          }
          if (isEqual(planData.measure, "salesActualYoY")) {
            yoyAmt = numberUtil.decimalAdd(yoyAmt, sumAmt, 2);
            yoyQty = numberUtil.decimalAdd(yoyQty, sum(planData.qty), 2);
          }
          if (isEqual(planData.measure, "demandPlan")) {
            demandPlanAmt = numberUtil.decimalAdd(demandPlanAmt, sumAmt, 2);
            demandPlanQty = numberUtil.decimalAdd(demandPlanQty, sum(planData.qty), 2);
          }
          if (isEqual(planData.measure, "budgetPlan")) {
            budgetAmt = numberUtil.decimalAdd(budgetAmt, sumAmt, 2);
            budgetQty = numberUtil.decimalAdd(budgetQty, sum(planData.qty), 2);
          }
        }

        const yoyAmtPer = getPer(salesActualAmt, yoyAmt);
        const yoyQtyPer = getPer(salesActualQty, yoyQty);
        const salesPlanAmtPer = getPer(salesActualAmt, demandPlanAmt);
        const salesPlanQtyPer = getPer(salesActualQty, demandPlanQty);
        const salesBudgetAmtPer = getPer(salesActualAmt, budgetAmt);
        const salesBudgetQtyPer = getPer(salesActualQty, budgetQty);

        const yoyAmtData = { hrchyNm: hrchyKey, stackBarChartData: [salesActualAmt, yoyAmt, yoyAmtPer], type: AMT };
        const yoyQtyData = { hrchyNm: hrchyKey, stackBarChartData: [salesActualQty, yoyQty, yoyQtyPer], type: QTY };
        const dpQtyData = { hrchyNm: hrchyKey, stackBarChartData: [salesActualQty, demandPlanQty, salesPlanQtyPer], type: QTY };
        const dpAmtata = { hrchyNm: hrchyKey, stackBarChartData: [salesActualAmt, demandPlanAmt, salesPlanAmtPer], type: AMT };
        const budgetQtyData = { hrchyNm: hrchyKey, stackBarChartData: [salesActualQty, budgetQty, salesBudgetQtyPer], type: QTY };
        const budgetAmtdata = { hrchyNm: hrchyKey, stackBarChartData: [salesActualAmt, budgetAmt, salesBudgetAmtPer], type: AMT };

        YOY.push(yoyAmtData, yoyQtyData);
        DP.push(dpAmtata, dpQtyData);
        BUDGET.push(budgetQtyData, budgetAmtdata);
      }
    }

    const yoyAll = getAllData(YOY, leafFlg, allName);
    const dpAll = getAllData(DP, leafFlg, allName);
    const budgetAll = getAllData(BUDGET, leafFlg, allName);

    setMaxValues(YOY);
    setMaxValues(DP);
    setMaxValues(BUDGET);

    if (!isEmpty(yoyAll)) {
      YOY.unshift(...yoyAll);
    }
    if (!isEmpty(dpAll)) {
      DP.unshift(...dpAll);
    }
    if (!isEmpty(budgetAll)) {
      BUDGET.unshift(...budgetAll);
    }

    return {
      yoy: getSortList(YOY, "YOY"),
      demand: getSortList(DP, "DP"),
      budget: getSortList(BUDGET, "BUDGET"),
    };
  };

  const allMeasures = useMemo(() => {
    return {
      qty: [SALES_ACTUAL, YOY, DEMAND_PLAN, FINANCE_PLAN, YOY_P, MOM_P, SALES_VS_PLAN_P, FIN_ACHIEVE_P, PLAN_VS_FIN_P],
      amt: [SALES_ACTUAL, SALES_ACTUAL_PROFIT, COGS, DEMAND_PLAN, DEMAND_PLAN_COGS, DEMAND_PLAN_GP,
        FINANCE_PLAN, FINANCE_PLAN_COGS, FINANCE_PLAN_GP, YOY, YOY_COGS, YOY_GP, MOM_P, MOM_GP_P,
        YOY_P, YOY_GP_P, GROSS_MARGIN_P, SALES_VS_PLAN_P, SALES_VS_PLAN_GP_P, SALES_VS_FIN_GP_P,
        FIN_ACHIEVE_P, PLAN_VS_FIN_GP_P, PLAN_VS_FIN_P],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getGridData = monthlyByTrendData => {
    const dataList = [];
    const header = monthlyByTrendData?.baseDate;
    const measureNms = difference(keys(monthlyByTrendData), ["key", "baseDate"]);

    for (const measure of measureNms) {
      const measureData = monthlyByTrendData[measure];
      if (size(measureData) > 0) {
        for (const rowData of measureData) {
          if (includes(allMeasures[rowData.type], measure)) {
            const rData = {};
            rData.measure = measure;
            rData.custHrchy = rowData.custHrchyNm;
            rData.itemHrchy = rowData.itemHrchyNm;

            if (rowData?.customQtyTotal) {
              rData.customQtyTotal = rowData.customQtyTotal;
            }
            if (rowData?.customAmtTotal) {
              rData.customAmtTotal = rowData.customAmtTotal;
            }

            for (let idx = 0; idx < header.length; idx++) {
              rData[header[idx]] = rowData.values?.[idx] ?? 0;
            }
            rData.type = rowData.type;
            dataList.push(rData);
          }
        }
      }
    }
    const gridData = groupBy(dataList, "type");
    gridData.baseDate = header;
    return gridData;
  };

  const getChartData = monthlyByTrendData => {
    const chartDataList = [];
    const header = monthlyByTrendData?.baseDate;
    const measureNms = difference(keys(monthlyByTrendData), ["key", "baseDate"]);

    for (let idx = 0; idx < header?.length ?? 0; idx++) {
      const cQtyData = {};
      const cAmtData = {};
      cQtyData.yyyymmdd = header[idx];
      cAmtData.yyyymmdd = header[idx];

      for (const measure of measureNms) {
        const measureData = monthlyByTrendData[measure];
        if (size(measureData) > 0) {
          for (const rowData of measureData) {
            if (includes(allMeasures[rowData.type], measure)) {
              if (isEqual(rowData.type, QTY)) {
                cQtyData.type = rowData.type;
                cQtyData[measure] = rowData?.values?.[idx];
              }
              if (isEqual(rowData.type, AMT)) {
                cAmtData.type = rowData.type;
                cAmtData[measure] = rowData?.values?.[idx];
              }
            }
          }
        }
      }
      chartDataList.push(cAmtData);
      chartDataList.push(cQtyData);
    }
    return groupBy(chartDataList, "type");
  };

  const getData = (ytdData, monthlyData, baseDate, custHrchyNm, itemHrchyNm) => {
    const custYTDdata = getYtdData(ytdData, custHrchyNm);
    const itemYTDdata = getYtdData(ytdData, itemHrchyNm, false);

    const trendGroupData = groupBy(monthlyData, "measure");
    const trendData = {};
    const customAmtTotalMeasures = [GROSS_MARGIN_P, SALES_VS_PLAN_P, SALES_VS_PLAN_GP_P, SALES_VS_FIN_GP_P,
      FIN_ACHIEVE_P, PLAN_VS_FIN_GP_P, PLAN_VS_FIN_P];
    const customQtyTotalMeasures = [SALES_VS_PLAN_P, FIN_ACHIEVE_P, PLAN_VS_FIN_P];

    // amt
    // grossMarginPer: (Total Actual Sales - Total COGS) ÷ Total Actual Sales
    // salesVsPlanPer: 1 + (Total Actual Sales − Total Demand Plan) ÷ TotalDemandPlan
    // salesVsPlanGPPer: 1 + (Total Gross Profit − Total Demand Plan Profit) ÷ Total Demand Plan Profit
    // salesVsBudgetGPPer: 1 + (Total Gross Profit − Total Finance Plan Profit) ÷ Total Finance Plan Profit
    // finAchievePerQty: 1 + (Total Actual Sales−Total Finance Plan) ÷ Total Finance Plan
    // planVsBudgetGPPer: Total Demand Plan Profit ÷ Total Finance Plan Profit
    // planVsBudgetPer: Total Demand Plan ÷ Total Finance Plan

    const formulas = {
      [GROSS_MARGIN_P]: (a, b) => a === 0 ? 0 : round(((a - b) / a), 2),
      [SALES_VS_PLAN_P]: (a, b) => b === 0 ? 0 : round(1 + ((a - b) / b), 2),
      [SALES_VS_PLAN_GP_P]: (a, b) => b === 0 ? 0 : round(1 + ((a - b) / b), 2),
      [SALES_VS_FIN_GP_P]: (a, b) => b === 0 ? 0 : round(1 + ((a - b) / b), 2),
      [FIN_ACHIEVE_P]: (a, b) => b === 0 ? 0 : round(1 + ((a - b) / b), 2),
      [PLAN_VS_FIN_GP_P]: (a, b) => b === 0 ? 0 : round((a / b), 2),
      [PLAN_VS_FIN_P]: (a, b) => b === 0 ? 0 : round((a / b), 2),
    };

    const sumByMeasure = (measureName, valueKey = AMT) => {
      const actualMeasure = [SALES_ACTUAL, SALES_ACTUAL_PROFIT];
      const arr = trendGroupData[measureName];
      if (!arr) return Array(baseDate.length).fill(0);
      if (!isArray(arr) || size(arr) === 0) return Array(baseDate.length).fill(0);

      const values = arr[0]?.[valueKey] || [];
      if (!isArray(values) || values.length === 0) return Array(baseDate.length).fill(0);

      // 현재 월(YYYY-MM) 계산
      const now = new Date();
      const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

      // baseDate에서 현재 월 시작 인덱스 찾기(없으면 전체 합산)
      const currentMonthIdx = baseDate.findIndex(d => String(d).slice(0, 7) === currentMonth);

      // 실제 측정치면 현재 월 제외하고 전월까지 합산, 그 외는 전체 합산
      if (actualMeasure.includes(measureName)) {
        const cutoff = currentMonthIdx >= 0 ? currentMonthIdx : values.length;
        return sum(values.slice(0, cutoff));
      }

      return sum(values);
    };

    const totalSalesActual = sumByMeasure(SALES_ACTUAL);
    const totalSalesActualQty = sumByMeasure(SALES_ACTUAL, QTY);
    const totalCogs = sumByMeasure(COGS);
    const totalGrossProfit = sumByMeasure(SALES_ACTUAL_PROFIT);
    const totalDemandPlan = sumByMeasure(DEMAND_PLAN);
    const totalDemandPlanQty = sumByMeasure(DEMAND_PLAN, QTY);
    const totalDemandPlanGP = sumByMeasure(DEMAND_PLAN_GP);
    const totalFinancePlan = sumByMeasure(FINANCE_PLAN);
    const totalFinancePlanQty = sumByMeasure(FINANCE_PLAN, QTY);
    const totalFinancePlanGP = sumByMeasure(FINANCE_PLAN_GP);

    const valueArrays = {
      [GROSS_MARGIN_P]: [totalSalesActual, totalCogs],
      [SALES_VS_PLAN_P]: [totalSalesActual, totalDemandPlan],
      [SALES_VS_PLAN_P + QTY]: [totalSalesActualQty, totalDemandPlanQty],
      [SALES_VS_PLAN_GP_P]: [totalGrossProfit, totalDemandPlanGP],
      [SALES_VS_FIN_GP_P]: [totalGrossProfit, totalFinancePlanGP],
      [FIN_ACHIEVE_P]: [totalSalesActual, totalFinancePlan],
      [FIN_ACHIEVE_P + QTY]: [totalSalesActualQty, totalFinancePlanQty],
      [PLAN_VS_FIN_GP_P]: [totalDemandPlanGP, totalFinancePlanGP],
      [PLAN_VS_FIN_P]: [totalDemandPlan, totalFinancePlan],
      [PLAN_VS_FIN_P + QTY]: [totalDemandPlanQty, totalFinancePlanQty]
    };

    const getCustomValue = (measureName, valType = null) => {
      const valueName = valType ? measureName + valType : measureName;
      const [arr1, arr2] = valueArrays[valueName] || [null, null];
      const fn = formulas[measureName];
      return fn ? fn(arr1, arr2) : null;
    };

    for (const measureNm of keys(trendGroupData)) {
      const measureData = trendGroupData[measureNm];

      let customAmtTotal = null;
      let customQtyTotal = null;

      if (includes(customAmtTotalMeasures, measureNm)) {
        customAmtTotal = getCustomValue(measureNm);
      }
      if (includes(customQtyTotalMeasures, measureNm)) {
        customQtyTotal = getCustomValue(measureNm, QTY);
      }

      for (const singleData of measureData) {
        const amt = {
          custHrchyNm: singleData.custHrchyNm,
          itemHrchyNm: singleData.itemHrchyNm,
          type: AMT,
          values: singleData.amt,
          customAmtTotal
        };
        const qty = {
          custHrchyNm: singleData.custHrchyNm,
          itemHrchyNm: singleData.itemHrchyNm,
          type: QTY,
          values: singleData.qty,
          customQtyTotal
        };

        trendData[measureNm] = [amt, qty];
      }
    }

    const monthlyGridData = getGridData({ baseDate, ...trendData });
    const monthlyChartData = getChartData({ baseDate, ...trendData });

    setStackBarData({ customerHrchy: custYTDdata, itemHrchy: itemYTDdata });
    setGridData(monthlyGridData);
    setChartData(monthlyChartData);
  };

  const retreieveDpDashboard = () => {
    const params = getRequestParams();
    dashboardService.getDp(params)
      .then(response => {
        const { data } = response;
        if (!isEmpty(data)) {
          setCurrency(data?.currency);
          getData(data?.ytd, data?.monthlyTrend, data?.baseDate, data?.custHrchyNm, data?.itemHrchyNm);
        }
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { filter: filterValue, measureType });
        reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...reduxState }));
      });
  };

  const defaultStackBarColumDefs = useMemo(() => [
    {
      headerName: "realCategory",
      field: "hrchyNm",
      flex: 2,
      type: ["nonEditableColumn", "defaultValueFormatter"],
    },
    {
      headerName: "",
      field: "stackBarChartData",
      cellRenderer: "stackBarChartCellRenderer",
      cellRendererParams: {
        scmModule: DP,
        currencyCd: currency
      },
      type: ["nonEditableColumn", "defaultValueFormatter"],
      flex: 4,
    },
  ],[DP, currency]);

  useEffect(() => {
    setInitialLoad(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialLoad) {
      retreieveDpDashboard();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);

  useEffect(() => {
    const reduxState = reduxUtil.createObjectState(viewName, { measureType });
    reduxDispatch(reduxUtil.updateViewState(reduxState));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureType]);

  // 데이터 기간 캡션(baseDate 첫~마지막, yyyy-MM). 통합 대시보드 상단바와 동일한 룩.
  const period = useMemo(() => baseDatesToPeriod(gridData?.baseDate), [gridData]);

  return (
    <>
      <FilterContainer>
        <CustomerHrchyTreeFilter
          viewName={viewName}
          onChangeFilter={setFilterValue}
          enableSelectParent
          styles={{ width: 290, ml: 0.5 }}
        />
        <ItemHrchyTreeFilter
          viewName={viewName}
          onChangeFilter={setFilterValue}
          enableSelectParent
          styles={{ width: 290 }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <TypeToggleButton
            toggleType="measure"
            initialValue={measureType}
            toggleValue={setMeasureType}
            styles={{ mb: 1 }}
          />
          <DashboardPeriodCaption period={period} />
        </Box>
      </FilterContainer>
      <DashboardComponent
        moduleCd={"DP"}
        title={title}
        viewName={viewName}
        measureType={measureType}
        allMeasures={allMeasures}
        stackBarColumDefs={defaultStackBarColumDefs}
        stackBarData={stackBarData}
        chartData={chartData}
        gridData={gridData}
        currencyCd={currency}
      />
    </>
  );
};

export default withTranslation()(DpDashboard);
